import type { LocalPublicationReceipt } from '@streamlain/doc-publication-contract';

import type { RowShardCells } from './data-source-contracts.js';

/**
 * A row whose creation is on its way to its shard doc. Until the publication
 * lands, cells written to the row collect here, and reads of the row answer
 * from here.
 */
export type PendingCreatedRow = {
  cells: RowShardCells;
  accepting: boolean;
  receipt: Promise<LocalPublicationReceipt | null>;
  /** Edits written after the publication started, sent once it lands. */
  followup?: PendingRowFollowup;
};

/**
 * One write per row waits for the creation receipt. Later keystrokes merge
 * into it until it starts, so an older keystroke is never sent, or read
 * back, after a newer one.
 */
export type PendingRowFollowup = {
  cells: RowShardCells;
  started: boolean;
  receipt: Promise<LocalPublicationReceipt | null>;
};

/** Stages from one storage store are prepared serially; other tabs still use actor/clock CAS. */
const pendingCreatedRows = new WeakMap<
  object,
  Map<string, PendingCreatedRow>
>();

export function pendingCreatedRowsOf(
  storageDoc: object
): Map<string, PendingCreatedRow> | undefined {
  return pendingCreatedRows.get(storageDoc);
}

export function ensurePendingCreatedRows(
  storageDoc: object
): Map<string, PendingCreatedRow> {
  let pending = pendingCreatedRows.get(storageDoc);
  if (!pending) {
    pendingCreatedRows.set(storageDoc, (pending = new Map()));
  }
  return pending;
}

export function readPendingCreatedRowCells(
  storageDoc: object,
  rowId: string
): RowShardCells | undefined {
  return pendingCreatedRows.get(storageDoc)?.get(rowId)?.cells;
}

/**
 * Cells of an existing row sent to the row writer but not yet back in the
 * live shard doc. Reads answer from here so an editor sees its own value
 * between the keystroke and the publication receipt.
 */
type PendingRowUpdate = {
  cells: RowShardCells;
  receipt: Promise<LocalPublicationReceipt | null>;
};

const pendingRowUpdates = new WeakMap<object, Map<string, PendingRowUpdate>>();

/** The latest receipt owns the row; an earlier one settling keeps it. */
export function recordPendingRowUpdate(
  storageDoc: object,
  rowId: string,
  cells: RowShardCells,
  receipt: Promise<LocalPublicationReceipt | null>,
  onRelease: () => void
): void {
  let rows = pendingRowUpdates.get(storageDoc);
  if (!rows) pendingRowUpdates.set(storageDoc, (rows = new Map()));
  const update = {
    cells: { ...rows.get(rowId)?.cells, ...structuredClone(cells) },
    receipt,
  };
  rows.set(rowId, update);
  const release = () => {
    if (rows.get(rowId) !== update) return;
    rows.delete(rowId);
    onRelease();
  };
  receipt.then(release, release);
}

export function readPendingRowUpdateCells(
  storageDoc: object,
  rowId: string
): RowShardCells | undefined {
  return pendingRowUpdates.get(storageDoc)?.get(rowId)?.cells;
}
